<script setup lang="ts">
import { computed, ref } from "vue";

import Button from "@/components/Reusables/Buttons/Button.vue";
import NumberInput from "@/components/Reusables/Inputs/NumberInput.vue";
import { useModalStore } from "@/stores/modals/modals.store";
import format from "@/services/Utils/format";

const props = defineProps<{
  label: string;
  weight: number;
  materialWeight?: number;
  weightUnit?: "kg" | "ton";
}>();

const modals = useModalStore();

const isTon = computed(() => props.weightUnit === "ton");
const unitLabel = computed(() => (isTon.value ? "ton" : "kg"));
const toDisplay = (kg: number) => (isTon.value ? kg / 1000 : kg);
const toKg = (display: number) => (isTon.value ? display * 1000 : display);

const editWeight = ref(toDisplay(props.weight));
const editPercentage = ref<number | null>(
  props.materialWeight && props.materialWeight > 0 ? parseFloat(((props.weight / props.materialWeight) * 100).toFixed(2)) : null
);

const displayMaterialWeight = computed(() => (props.materialWeight != null ? toDisplay(props.materialWeight) : undefined));

const handleWeightChange = (newWeight: number | null) => {
  editWeight.value = newWeight ?? 0;
  if (props.materialWeight && props.materialWeight > 0 && newWeight !== null) {
    const weightInKg = toKg(newWeight);
    editPercentage.value = parseFloat(((weightInKg / props.materialWeight) * 100).toFixed(2));
  } else {
    editPercentage.value = null;
  }
};

const handlePercentageChange = (newPercentage: number | null) => {
  editPercentage.value = newPercentage;
  if (props.materialWeight && props.materialWeight > 0 && newPercentage !== null) {
    const weightInKg = parseFloat(((newPercentage / 100) * props.materialWeight).toFixed(2));
    editWeight.value = parseFloat(toDisplay(weightInKg).toFixed(2));
  } else {
    editWeight.value = 0;
  }
};

const handleSave = () => {
  modals.closeTop({ weight: toKg(editWeight.value) });
};

const handleCancel = () => {
  modals.closeTop();
};
</script>

<template>
  <div class="flex flex-col gap-5">
    <h2 class="text-type-muted t-sh4 flex w-full items-center gap-2 pl-3">
      <font-awesome-icon
        icon="fa-solid fa-cubes"
        class="text-slate-500" />
      Edit Waste Weight
    </h2>

    <div class="flex flex-col gap-3 px-1">
      <p class="t-small text-type-muted">
        Editing waste weight for <strong class="text-type-default">{{ label }}</strong>
      </p>

      <div
        class="grid gap-3"
        :class="displayMaterialWeight !== undefined ? 'grid-cols-[1fr_auto_1fr]' : 'grid-cols-[1fr]'">
        <NumberInput
          :value="editWeight"
          :label="`Weight (${unitLabel})`"
          placeholder="0"
          color="primary"
          :positive="true"
          @update:value="handleWeightChange" />
        <div
          v-if="displayMaterialWeight !== undefined"
          class="flex h-10 items-center self-end">
          <div class="h-6 w-px bg-slate-200"></div>
        </div>
        <NumberInput
          v-if="displayMaterialWeight !== undefined"
          :value="editPercentage"
          label="Percentage"
          placeholder="0"
          color="primary"
          :max="100"
          :positive="true"
          :max-decimals="2"
          @update:value="handlePercentageChange" />
      </div>

      <div
        v-if="displayMaterialWeight !== undefined"
        class="t-xs flex items-center justify-end py-2 text-slate-500">
        <span>
          Total material weight:
          <strong class="text-slate-700">{{ format.decimalNumber(displayMaterialWeight, 2) }} {{ unitLabel }}</strong>
        </span>
      </div>
    </div>

    <div class="mt-auto flex justify-between px-1">
      <Button
        label="Cancel"
        color="button"
        @click="handleCancel" />
      <Button
        label="Save"
        color="primary"
        @click="handleSave" />
    </div>
  </div>
</template>
